import type { Role } from '../net/protocol';
import { autoNameForRole } from '../names';
import { ensureY2kStyle, isY2kActive } from './y2k_style';

// Team picker. Previously a full-screen splash shown at boot; now a
// lightweight modal shown when the player clicks a server in the
// matchmaker. Boot just reads the saved role + name and drops straight
// into the lobby so users walk around before committing to a team.

const PREFERRED_KEY = 'smp.preferredRole';
const NAME_KEY = 'smp.username';
// Matches the constants in src/lobby/last_match.ts. Duplicated inline
// here so team_picker doesn't pull the whole lobby chunk into the
// boot-critical graph. Kept in sync with lobby/last_match.ts.
const LAST_MATCH_KEY = 'smp:lastMatch:v1';
const LAST_SNAP_KEY = 'smp:lastMatch:selfSnap:v1';
// Tighter than lobby/last_match.ts TTL: real reconnects fire within a
// few seconds. Anything older is a new session and should pass through
// the matchmaker + server-time picker again. 60 s is a generous budget
// for WS retry + page reload.
const REJOIN_SKIP_TTL_MS = 60 * 1000;

function getSavedName(): string | null {
  const v = localStorage.getItem(NAME_KEY);
  return v && v.length > 0 ? v : null;
}

// True ONLY when a genuine mid-match reconnect is in flight. Both the
// match stash AND the self-snapshot must exist, pointing at the same
// room, with the match record under REJOIN_SKIP_TTL_MS old. Fresh
// browsers that never played a match never hit this branch.
function hasLiveRejoin(): boolean {
  try {
    const rawMatch = localStorage.getItem(LAST_MATCH_KEY);
    if (!rawMatch) return false;
    const match = JSON.parse(rawMatch) as { at?: number; room?: string };
    if (typeof match?.at !== 'number') return false;
    if (Date.now() - match.at > REJOIN_SKIP_TTL_MS) return false;
    const rawSnap = localStorage.getItem(LAST_SNAP_KEY);
    if (!rawSnap) return false;
    const snap = JSON.parse(rawSnap) as { room?: string };
    if (!snap?.room || snap.room !== match.room) return false;
    return true;
  } catch {
    return false;
  }
}

function rejoinRole(): Role | null {
  try {
    const raw = localStorage.getItem(LAST_SNAP_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { snap?: { role?: string } };
      const r = parsed?.snap?.role;
      if (r === 'int' || r === 'ext') return r;
    }
  } catch { /* noop */ }
  return getPreferredRole();
}

export function getPreferredRole(): Role | null {
  const v = localStorage.getItem(PREFERRED_KEY);
  return v === 'ext' || v === 'int' ? v : null;
}

export function setPreferredRole(role: Role | null): void {
  if (role) localStorage.setItem(PREFERRED_KEY, role);
  else localStorage.removeItem(PREFERRED_KEY);
}

export function getSavedOrAutoName(role: Role, deviceId: string): string {
  return getSavedName() ?? autoNameForRole(role, deviceId);
}

// Boot-time resolver. Never shows UI — picker now fires at matchmaker
// click time via `openInMatchTeamPicker`. Honors `?role=` URL override
// for tests, the rejoin stash for mid-match refresh, and falls through
// to saved preferred role or 'int' default.
export function promptTeam(
  _parent: HTMLElement,
  deviceId: string,
  cb: (name: string, role: Role) => void,
): void {
  const params = new URL(location.href).searchParams;
  const qpRole = params.get('role');
  if (qpRole === 'ext' || qpRole === 'int') {
    setPreferredRole(qpRole);
    const nm = getSavedName() ?? autoNameForRole(qpRole, deviceId);
    localStorage.setItem(NAME_KEY, nm);
    cb(nm, qpRole);
    return;
  }

  if (hasLiveRejoin()) {
    const role = rejoinRole() ?? 'int';
    const nm = getSavedName() ?? autoNameForRole(role, deviceId);
    localStorage.setItem(NAME_KEY, nm);
    cb(nm, role);
    return;
  }

  // Lobby spawn role is randomized per session. Saved preferred role is
  // intentionally NOT used here — the in-match team picker
  // (openInMatchTeamPicker) is the only commit point. Players were
  // always landing as virgin because the legacy fallback hardcoded
  // 'int'; randomizing per boot makes the lobby crowd feel mixed.
  const bootRole: Role = Math.random() < 0.5 ? 'ext' : 'int';
  const bootName = getSavedName() ?? autoNameForRole(bootRole, deviceId);
  localStorage.setItem(NAME_KEY, bootName);
  cb(bootName, bootRole);
}

// Lightweight in-match team picker. Called from the matchmaker flow
// right after the user clicks a server, BEFORE `enterMatch` fires.
// Passes the chosen role back via `cb`; caller sets preferredRole on
// the net client and swaps the room. ESC / click-outside cancels.
export function openInMatchTeamPicker(
  parent: HTMLElement,
  cb: (role: Role) => void,
  onCancel?: () => void,
): () => void {
  let closed = false;
  const close = (chosen: Role | null) => {
    if (closed) return;
    closed = true;
    backdrop.removeEventListener('click', onBackdrop);
    removeEventListener('keydown', onKey);
    backdrop.remove();
    if (chosen) cb(chosen);
    else onCancel?.();
  };

  const y2k = isY2kActive();
  if (y2k) ensureY2kStyle();

  const backdrop = document.createElement('div');
  backdrop.className = 'ui interactive';
  // Fully opaque backdrop — this modal is often the first frame a
  // fresh user sees after the title splash fades, and any lobby
  // bleed-through read as a jarring split-second "flash" on cold
  // boot. Use the same navy/wordmark gradient as the splash so the
  // transition feels like one continuous screen.
  backdrop.style.cssText = `
    position: fixed; inset: 0;
    background: ${y2k
      ? 'radial-gradient(ellipse at 50% 120%, #ff1493 0%, #0a0220 55%, #03050f 100%)'
      : 'radial-gradient(ellipse at 30% 40%, rgba(96,165,250,.22) 0%, transparent 55%), radial-gradient(ellipse at 70% 60%, rgba(251,146,60,.22) 0%, transparent 55%), #03070f'};
    display: flex; align-items: center; justify-content: center;
    z-index: 9999;
    animation: team-picker-fade .18s ease-out;
  `;

  const card = document.createElement('div');
  if (y2k) {
    card.className = 'y2k-card';
    card.style.cssText = `
      position: relative;
      min-width: 420px; max-width: 92vw;
      padding: 30px 32px 26px;
      text-align: center;
      z-index: 1;
    `;
    card.innerHTML = `
      <div class="y2k-star" style="top:-10px; left:-10px;"></div>
      <div class="y2k-star" style="top:-8px; right:-6px; width:16px; height:16px;"></div>
      <div class="y2k-star" style="bottom:40px; left:-14px; width:14px; height:14px;"></div>
      <div class="y2k-chip" style="position:relative;z-index:2;margin-bottom:10px;">HYPERGRID · MATCHMAKING</div>
      <div class="y2k-heading" style="position:relative;z-index:2;margin-bottom:4px;">
        CHOOSE YOUR TEAM
      </div>
      <div class="y2k-subtle" style="position:relative;z-index:2;margin-bottom:22px;">
        Joining match server…
      </div>
      <div id="team-pick-row" style="position:relative;z-index:2;display:flex;gap:14px;justify-content:center;">
        <button class="y2k-btn y2k-btn-virgin" data-team="int" style="flex:1;">
          <div style="font-size:24px;line-height:1;margin-bottom:4px;">🥷</div>
          <div>VIRGIN</div>
          <div style="font:600 9px ui-monospace;opacity:.75;letter-spacing:1px;margin-top:6px;">
            Ring attack · Hood
          </div>
        </button>
        <button class="y2k-btn y2k-btn-inf" data-team="ext" style="flex:1;">
          <div style="font-size:24px;line-height:1;margin-bottom:4px;">🎉</div>
          <div>CHAD</div>
          <div style="font:600 9px ui-monospace;opacity:.75;letter-spacing:1px;margin-top:6px;">
            Circle attack · Leap
          </div>
        </button>
      </div>
      <div class="y2k-marquee">
        <div class="y2k-marquee-inner">
          <span>★</span><span class="pink">CLICK TO PLAY</span><span>★</span><span class="cyan">2026</span><span>★</span><span class="pink">NO LOGIN</span><span>★</span><span class="cyan">MULTIPLAYER</span><span>★</span>
          <span>★</span><span class="pink">CLICK TO PLAY</span><span>★</span><span class="cyan">2026</span><span>★</span><span class="pink">NO LOGIN</span><span>★</span><span class="cyan">MULTIPLAYER</span><span>★</span>
        </div>
      </div>
      <button data-team="back" class="y2k-btn" style="
        margin: 12px auto 0; display:block;
        padding:8px 18px; font:700 11px ui-monospace; letter-spacing:1.4px;
        position:relative; z-index:2; min-width:auto;
      ">← BACK TO MATCHMAKER</button>
      <div class="y2k-subtle" style="position:relative;z-index:2;margin-top:10px;">
        ESC TO CANCEL
      </div>
    `;
  } else {
    card.style.cssText = `
      min-width: 380px; max-width: 92vw;
      background: linear-gradient(145deg, #0f172a, #1e293b);
      border: 2px solid #475569;
      border-radius: 18px;
      padding: 28px 32px;
      box-shadow: 0 24px 64px rgba(0,0,0,.6);
      color: #e5e7eb;
      font-family: system-ui, sans-serif;
      text-align: center;
    `;
    card.innerHTML = `
      <div style="font:900 22px system-ui; letter-spacing: 2px; color:#fde047; margin-bottom:6px;">
        CHOOSE YOUR TEAM
      </div>
      <div style="font:600 11px system-ui; opacity:.65; letter-spacing:1.5px; margin-bottom:22px;">
        Joining match server…
      </div>
      <div id="team-pick-row" style="display:flex; gap:14px; justify-content:center;">
        <button data-team="int" style="
          flex:1; padding:18px 22px; border-radius:14px; cursor:pointer;
          border:2px solid #60a5fa;
          background:linear-gradient(145deg, rgba(96,165,250,.16), rgba(30,58,138,.32));
          color:#93c5fd; font:900 14px system-ui; letter-spacing:1.6px;
          transition: transform .1s, box-shadow .1s;
        ">
          🥷<br/>VIRGIN
          <div style="font:600 9px system-ui; opacity:.75; letter-spacing:1px; margin-top:6px;">
            Ring attack · Hood
          </div>
        </button>
        <button data-team="ext" style="
          flex:1; padding:18px 22px; border-radius:14px; cursor:pointer;
          border:2px solid #fb923c;
          background:linear-gradient(145deg, rgba(251,146,60,.16), rgba(154,52,18,.32));
          color:#fdba74; font:900 14px system-ui; letter-spacing:1.6px;
          transition: transform .1s, box-shadow .1s;
        ">
          🎉<br/>CHAD
          <div style="font:600 9px system-ui; opacity:.75; letter-spacing:1px; margin-top:6px;">
            Circle attack · Leap
          </div>
        </button>
      </div>
      <button data-team="back" style="
        margin: 16px auto 0; display:block;
        padding:8px 18px; border-radius:10px; cursor:pointer;
        border:1px solid rgba(148,163,184,.4);
        background: rgba(15,23,42,.6);
        color:#cbd5e1; font:700 11px system-ui; letter-spacing:1.4px;
        transition: background .14s, border-color .14s;
      ">← BACK TO MATCHMAKER</button>
      <div style="font:600 10px system-ui; opacity:.4; margin-top:10px; letter-spacing:1px;">
        ESC TO CANCEL
      </div>
    `;
  }
  backdrop.appendChild(card);
  parent.appendChild(backdrop);

  card.querySelectorAll<HTMLButtonElement>('button[data-team]').forEach((btn) => {
    const team = btn.dataset.team;
    if (!y2k && team !== 'back') {
      btn.addEventListener('mouseenter', () => {
        btn.style.transform = 'translateY(-2px) scale(1.02)';
        btn.style.boxShadow = '0 10px 30px rgba(0,0,0,.4)';
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.transform = '';
        btn.style.boxShadow = '';
      });
    }
    btn.addEventListener('click', () => {
      if (team === 'back') {
        // Cancel path — same exit as ESC. Caller's onCancel callback
        // is responsible for re-opening the matchmaker.
        close(null);
        return;
      }
      setPreferredRole(team as Role);
      close(team as Role);
    });
  });

  // Backdrop clicks are SWALLOWED — the team picker is a required
  // choice on cold boot. Misclicking outside the card used to kick the
  // player to the lobby; now it's a no-op so players have to commit
  // via button / keybind / ESC.
  const onBackdrop = (e: MouseEvent) => {
    if (e.target === backdrop) e.stopPropagation();
  };
  backdrop.addEventListener('click', onBackdrop);

  const onKey = (e: KeyboardEvent) => {
    if (e.code === 'Escape') { e.preventDefault(); close(null); }
    else if (e.code === 'Digit1' || e.code === 'KeyO') close('int');
    else if (e.code === 'Digit2' || e.code === 'KeyI') close('ext');
  };
  addEventListener('keydown', onKey);

  return () => close(null);
}
